import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PushPromptCard from '../components/PushPromptCard';
import LoginToBook from '../components/LoginToBook';
import { useAuth } from '../App';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return <div className="alert alert-error">Payment form failed to load. Please refresh the page.</div>;
    return this.props.children;
  }
}

const PACK_COLOURS = { restore: '#FF6200', elevate: '#1565C0', breakthrough: '#6A1B9A' };
const PACK_SAVINGS = { restore: '~10%', elevate: '~15%', breakthrough: '~20%' };

function PackCard({ pack, onSelect, selected }) {
  const colour = PACK_COLOURS[pack.slug] || '#FF6200';
  const isSelected = selected?.id === pack.id;
  return (
    <button type="button" onClick={() => onSelect(pack)} style={{
      border: `2.5px solid ${isSelected ? colour : 'var(--grey-300)'}`,
      borderRadius: 14, padding: 20, background: isSelected ? `${colour}10` : '#fff',
      width: '100%', textAlign: 'left', transition: 'all 0.15s', cursor: 'pointer',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1.05rem', color: colour }}>{pack.name}</div>
          <div style={{ fontSize: '2rem', fontWeight: 900, marginTop: 4 }}>{pack.points} <span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--grey-700)' }}>pts</span></div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>£{(pack.price_pence / 100).toFixed(0)}</div>
          <div style={{ background: colour, color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, marginTop: 4 }}>Save {PACK_SAVINGS[pack.slug]}</div>
        </div>
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--grey-500)', marginTop: 10 }}>Valid 12 months · Points never expire mid-booking</div>
    </button>
  );
}

function CheckoutForm({ pack, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true); setError('');
    try {
      const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
        type: 'card', card: elements.getElement(CardElement),
      });
      if (pmErr) { setError(pmErr.message); setLoading(false); return; }

      const res = await api.purchasePack({ packId: pack.id, stripePaymentMethodId: paymentMethod.id });

      // The bank wants the cardholder to verify (3D Secure). Present the
      // challenge in-app, then tell the server to credit the points.
      if (res && res.requiresAction) {
        const { error: actionErr, paymentIntent } = await stripe.confirmCardPayment(res.clientSecret);
        if (actionErr) {
          setError(actionErr.message || 'We couldn\'t verify your card with your bank. Please try again.');
          setLoading(false);
          return;
        }
        if (!paymentIntent || paymentIntent.status !== 'succeeded') {
          setError('Your payment wasn\'t completed. Please try again.');
          setLoading(false);
          return;
        }
        await api.finalizePack({ paymentIntentId: paymentIntent.id });
      }

      onSuccess(pack);
    } catch (err) {
      setError(err.error || 'Payment failed. Please try again.');
    }
    setLoading(false);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <CardElement options={{ hidePostalCode: true, style: { base: { fontSize: '16px', color: '#1A1A1A', '::placeholder': { color: '#9E9E9E' } } } }} />
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <button className="btn btn-primary btn-full" type="submit" disabled={loading || !stripe}>
        {loading ? 'Processing…' : `Pay £${(pack.price_pence / 100).toFixed(2)}`}
      </button>
    </form>
  );
}

export default function PacksPage() {
  const nav = useNavigate();
  const { session } = useAuth();
  const [packs, setPacks]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [success, setSuccess]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const checkoutRef = React.useRef(null);

  function handleSelectPack(pack) {
    setSelected(pack);
    setTimeout(() => {
      if (checkoutRef.current) {
        checkoutRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  useEffect(() => {
    api.getPacks().then(p => setPacks(p.packs || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (success) return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
      <h2 style={{ fontWeight: 800, marginBottom: 8 }}>{success.points} points added!</h2>
      <p style={{ color: 'var(--grey-700)', marginBottom: 24 }}>Your {success.name} is now active. Start booking treatments.</p>

      <PushPromptCard moment="pack_purchase" />

      <button className="btn btn-primary" onClick={() => nav('/wellness')}>Book a Treatment</button>
    </div>
  );

  return (
    <div className="page">
      <h1 className="page-title">Buy Points</h1>
      <p style={{ color: 'var(--grey-700)', marginBottom: 20, fontSize: '0.9rem' }}>
        Choose a points pack and spend them on any wellness treatment. The bigger the pack, the bigger the saving.
      </p>

      {/* Pack selector */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {packs.length === 0 && <p style={{ color: 'var(--grey-500)', textAlign: 'center', padding: 24 }}>Loading packs…</p>}
        {packs.map(p => <PackCard key={p.id} pack={p} onSelect={handleSelectPack} selected={selected} />)}
      </div>

      {/* Pay as you go note */}
      <div className="alert alert-success" style={{ marginBottom: 24 }}>
        💡 No pack? You can also pay per treatment at standard rates when booking.
      </div>

      {/* Checkout — guests are prompted to log in instead */}
      {selected && (
        session ? (
          <ErrorBoundary>
            <Elements stripe={stripePromise}>
              <div ref={checkoutRef} className="card" style={{ padding: 20 }}>
                <h3 style={{ fontWeight: 700, marginBottom: 4 }}>Pay for {selected.name}</h3>
                <p style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginBottom: 16 }}>
                  {selected.points} points · £{(selected.price_pence / 100).toFixed(2)} · valid 12 months
                </p>
                <CheckoutForm pack={selected} onSuccess={p => setSuccess(p)} />
              </div>
            </Elements>
          </ErrorBoundary>
        ) : (
          <div ref={checkoutRef}>
            <LoginToBook message="Log in or create a free account to buy a points pack." />
          </div>
        )
      )}
    </div>
  );
}
